import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { HackerZoomConfig, ScreenBox } from "../scenario/types";
import { audio } from "../lib/audio";
import { SkipButton } from "../ui/SkipButton";

/**
 * "The Influencer" arc — the hacker's SECOND look, glued to the same
 * hacker-room `screenQuad` as `MitmReveal.tsx` (same `screenBox` projector
 * prop/mechanism). He scrolls the same public post the learner just watched
 * get posted, then the view auto-zooms into one photo's background where a
 * laptop screen (and the confidential document on it) becomes visible —
 * the OSINT pivot into a targeted spear-phish, using only a second,
 * pre-cropped composite image of that one photo (no new 3D asset).
 */
export function HackerZoom({
  config,
  onDone,
  screenBox,
}: {
  config: HackerZoomConfig;
  onDone: () => void;
  screenBox?: ScreenBox | null;
}) {
  const [lineIdx, setLineIdx] = useState(0);
  const [scrollIdx, setScrollIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [ready, setReady] = useState(false);
  const started = useRef(false);
  const skipped = useRef(false);
  const mounted = useRef(true);

  const photos = config.photos ?? [];
  const targetIdx = Math.max(0, Math.min(config.targetPhotoIndex ?? 0, photos.length - 1));

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    audio.startExfilAlarm();
    let cancelled = false;
    (async () => {
      const lines = config.narration ?? [];
      const voice = config.voiceId ?? "onyx";
      const lang = config.language ?? "en-US";
      void Promise.all(lines.map((t) => audio.prefetchSpeech(t, { voice, lang })));
      for (let i = 0; i < lines.length; i++) {
        if (cancelled || skipped.current || !mounted.current) return;
        setLineIdx(i);
        // Scroll roughly one photo per line, holding on the target photo for
        // the reveal line, then swap to the zoomed composite.
        const isRevealLine = i === lines.length - 1;
        setScrollIdx(isRevealLine ? targetIdx : Math.min(i, photos.length - 1));
        if (isRevealLine) {
          await new Promise((r) => window.setTimeout(r, 500));
          if (cancelled || skipped.current || !mounted.current) return;
          setZoomed(true);
          audio.dataBlip();
        }
        await audio.speak(lines[i], { voice, lang });
        if (cancelled || skipped.current || !mounted.current) return;
        await new Promise((r) => window.setTimeout(r, 250));
      }
      if (cancelled || skipped.current || !mounted.current) return;
      setReady(true);
      audio.stopExfilAlarm();
      audio.breachAlert();
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
      audio.stopExfilAlarm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    if (skipped.current) return;
    skipped.current = true;
    audio.cancelSpeech();
    audio.stopExfilAlarm();
    setLineIdx(Math.max(0, (config.narration ?? []).length - 1));
    setScrollIdx(targetIdx);
    setZoomed(true);
    setReady(true);
  };

  const glued = !!(screenBox && screenBox.visible);
  const wrapStyle: CSSProperties | undefined = glued
    ? {
        left: `${screenBox!.leftPct}%`,
        top: `${screenBox!.topPct}%`,
        width: `${screenBox!.widthPct}%`,
        height: `${screenBox!.heightPct}%`,
        right: "auto",
        bottom: "auto",
        transform: "none",
        alignItems: "stretch",
        justifyContent: "stretch",
      }
    : undefined;

  return (
    <div className={`mitm-wrap hzoom-wrap ${glued ? "mitm-wrap--glued" : ""}`} style={wrapStyle} role="alertdialog" aria-label="Reconnaissance">
      <div className="mitm-card hzoom-card">
        <div className="mitm-head">
          <span className="mitm-dot" /> {zoomed ? "ANALYZING IMAGE" : "OSINT — SOCIAL MEDIA"}
        </div>

        <div className="hzoom-stage">
          {!zoomed ? (
            <img className="hzoom-img" src={photos[scrollIdx]?.url} alt={photos[scrollIdx]?.alt ?? ""} />
          ) : (
            <div className="hzoom-zoomwrap">
              <img className="hzoom-img hzoom-img--zoomed" src={config.zoomImageUrl} alt="Zoomed detail" />
              <div className="hzoom-cropbox" aria-hidden />
            </div>
          )}
        </div>

        {(config.narration ?? []).length > 0 && (
          <div className="hzoom-line" key={lineIdx}>
            {config.narration[lineIdx]}
          </div>
        )}

        <div className="cq-continue-group mitm-continue-group">
          <button type="button" className="btn mitm-continue" disabled={!ready} onClick={onDone}>
            {ready ? config.continueLabel ?? "Continue" : "\u2026"}
          </button>
          {!ready && <SkipButton onClick={skip} label="Skip explanation" />}
        </div>
      </div>
    </div>
  );
}
